import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  InformationCircleIcon,
  AcademicCapIcon,
  UserGroupIcon,
  CheckCircleIcon,
  PlusIcon,
  ChartBarIcon,
  CalendarIcon,
  DocumentCheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { CompetencySubnav } from "../../../../components/dashboard/hse/competency/CompetencySubnav";

/**
 * HSE/KS Competency Management Guide
 * Visual guide showing how the competency management system works
 */
export const HseKsCompetencyGuide = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Define the structure nodes for visualization
  const structureNodes = [
    {
      id: "company",
      type: "company",
      title: t("Firma"),
      description: t("Ditt firma"),
      x: 100,
      y: 50,
      width: 200,
      height: 80,
      color: "#3B82F6",
    },
    {
      id: "definition",
      type: "definition",
      title: t("Kompetansedefinisjon"),
      description: t("FSE-Kurs, Førerkort klasse B, etc."),
      x: 400,
      y: 50,
      width: 220,
      height: 100,
      color: "#10B981",
      parent: "company",
    },
    {
      id: "role",
      type: "role",
      title: t("Rolle"),
      description: t("Prosjektleder, Montør, etc."),
      x: 700,
      y: 50,
      width: 200,
      height: 100,
      color: "#8B5CF6",
      parent: "company",
    },
    {
      id: "requirement",
      type: "requirement",
      title: t("Kompetansekrav"),
      description: t("Påkrevd kompetanse per rolle"),
      x: 550,
      y: 200,
      width: 200,
      height: 100,
      color: "#F59E0B",
      parent: "role",
    },
    {
      id: "competency",
      type: "competency",
      title: t("Ansatt kompetanse"),
      description: t("Registrert kompetanse for ansatte"),
      x: 400,
      y: 350,
      width: 220,
      height: 100,
      color: "#EF4444",
      parent: "definition",
    },
    {
      id: "employee",
      type: "employee",
      title: t("Ansatt"),
      description: t("Bruker med kompetanse"),
      x: 100,
      y: 350,
      width: 200,
      height: 100,
      color: "#06B6D4",
      parent: "competency",
    },
  ];

  // Define connections between nodes
  const connections = [
    { from: "company", to: "definition", label: t("Har definisjoner") },
    { from: "company", to: "role", label: t("Har roller") },
    { from: "role", to: "requirement", label: t("Har krav") },
    { from: "definition", to: "requirement", label: t("Påkrevd for") },
    { from: "definition", to: "competency", label: t("Tildelt som") },
    { from: "employee", to: "competency", label: t("Har kompetanse") },
  ];

  // Get node position
  const getNodePosition = (nodeId) => {
    const node = structureNodes.find((n) => n.id === nodeId);
    if (!node) return { x: 0, y: 0 };
    return {
      x: node.x + node.width / 2,
      y: node.y + node.height / 2,
    };
  };

  // Calculate connection path
  const calculatePath = (fromId, toId) => {
    const from = getNodePosition(fromId);
    const to = getNodePosition(toId);
    const midX = (from.x + to.x) / 2;
    return `M ${from.x} ${from.y} L ${midX} ${from.y} L ${midX} ${to.y} L ${to.x} ${to.y}`;
  };

  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);

  const handleNodeClick = (node) => {
    setSelectedNode(node.id === selectedNode?.id ? null : node);
  };

  const handleNodeHover = (node) => {
    setHoveredNode(node);
  };

  const handleNodeLeave = () => {
    setHoveredNode(null);
  };

  // Information sections
  const informationSections = [
    {
      id: "overview",
      title: t("Oversikt"),
      icon: InformationCircleIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {t("Kompetansehåndteringssystemet hjelper deg med å spore og administrere kompetanse for alle ansatte i organisasjonen din.")}
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">{t("Hovedkomponenter:")}</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
              <li><strong>{t("Kompetansedefinisjoner:")}</strong> {t("Definer hvilke kompetanser som finnes (f.eks. FSE-Kurs, Førerkort klasse B)")}</li>
              <li><strong>{t("Roller:")}</strong> {t("Definer roller i organisasjonen (f.eks. Prosjektleder, Montør)")}</li>
              <li><strong>{t("Kompetansekrav:")}</strong> {t("Knytt påkrevde kompetanser til roller")}</li>
              <li><strong>{t("Ansatt kompetanse:")}</strong> {t("Registrer hvilke ansatte som har hvilke kompetanser")}</li>
            </ol>
          </div>
        </div>
      ),
    },
    {
      id: "definitions",
      title: t("Kompetansedefinisjoner"),
      icon: AcademicCapIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {t("Kompetansedefinisjoner er maler for hvilke typer kompetanse som finnes i organisasjonen din.")}
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-2">{t("Eksempler:")}</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-green-800">
              <li>{t("FSE-Kurs (Felles Sikkerhetskurs)")}</li>
              <li>{t("Førerkort klasse B")}</li>
              <li>{t("Førstehjelpskurs")}</li>
              <li>{t("Kranførerbevis")}</li>
              <li>{t("VVS-sertifikat")}</li>
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {[
              { type: "cv", name: t("CV"), description: t("Erfaring og bakgrunn") },
              { type: "certificate", name: t("Sertifikat"), description: t("Offisielle sertifikater") },
              { type: "course", name: t("Kurs"), description: t("Fullførte kurs") },
              { type: "license", name: t("Lisens"), description: t("Offisielle lisenser") },
              { type: "other", name: t("Annet"), description: t("Andre typer kompetanse") },
            ].map((type) => (
              <div key={type.type} className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="font-semibold text-sm text-gray-900">{type.name}</div>
                <div className="text-xs text-gray-600 mt-1">{type.description}</div>
              </div>
            ))}
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-yellow-900 mb-2">{t("Fornyelse:")}</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-yellow-800">
              <li>{t("Noen kompetanser krever fornyelse (f.eks. FSE-Kurs hvert 3. år)")}</li>
              <li>{t("Sett fornyelsesintervall i dager for å spore utløp")}</li>
              <li>{t("Systemet varsler automatisk når kompetanse utløper snart")}</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: "roles",
      title: t("Roller og matrise"),
      icon: UserGroupIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {t("Roller definerer hvilke kompetanser som er påkrevd for forskjellige stillinger i organisasjonen.")}
          </p>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-semibold text-purple-900 mb-2">{t("Eksempler:")}</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-purple-800">
              <li><strong>{t("Prosjektleder:")}</strong> {t("FSE-Kurs, Ledelseskurs")}</li>
              <li><strong>{t("Montør:")}</strong> {t("FSE-Kurs, Førstehjelpskurs")}</li>
              <li><strong>{t("Kranfører:")}</strong> {t("FSE-Kurs, Kranførerbevis")}</li>
              <li><strong>{t("VVS-tekniker:")}</strong> {t("FSE-Kurs, VVS-sertifikat")}</li>
            </ul>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-blue-900 mb-2">{t("Kompetansematrise:")}</h4>
            <p className="text-sm text-blue-800 mb-2">
              {t("Kompetansematrisen viser en oversikt over hvilke ansatte som har de påkrevde kompetansene for sine roller.")}
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-blue-800">
              <li>{t("Grønn: Ansatt har alle påkrevde kompetanser")}</li>
              <li>{t("Gul: Ansatt mangler noen påkrevde kompetanser")}</li>
              <li>{t("Rød: Ansatt mangler flere påkrevde kompetanser")}</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: "employees",
      title: t("Ansatt kompetanse"),
      icon: UserGroupIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {t("Registrer hvilke ansatte som har hvilke kompetanser, inkludert utstedelses- og utløpsdatoer.")}
          </p>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <h4 className="font-semibold text-indigo-900 mb-2">{t("Status typer:")}</h4>
            <div className="space-y-2">
              {[
                { status: "active", name: t("Aktiv"), description: t("Kompetanse er gyldig"), color: "bg-green-100 text-green-800" },
                { status: "expiring_soon", name: t("Utløper snart"), description: t("Utløper innen 30 dager"), color: "bg-yellow-100 text-yellow-800" },
                { status: "expired", name: t("Utløpt"), description: t("Kompetanse har utløpt"), color: "bg-red-100 text-red-800" },
                { status: "permanent", name: t("Permanent"), description: t("Kompetanse utløper ikke"), color: "bg-gray-100 text-gray-800" },
              ].map((status) => (
                <div key={status.status} className={`border rounded-lg p-3 ${status.color}`}>
                  <div className="font-semibold text-sm">{status.name}</div>
                  <div className="text-xs mt-1">{status.description}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-yellow-900 mb-2">{t("Viktig:")}</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-yellow-800">
              <li>{t("Registrer utstedelsesdato for å spore alder på kompetanse")}</li>
              <li>{t("Sett utløpsdato hvis kompetansen har en utløpsdato")}</li>
              <li>{t("La utløpsdato stå tom for permanente kompetanser")}</li>
              <li>{t("Systemet oppdaterer automatisk status basert på utløpsdato")}</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: "workflow",
      title: t("Arbeidsflyt"),
      icon: CheckCircleIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {t("Følg disse trinnene for å sette opp kompetansehåndtering i organisasjonen din:")}
          </p>
          <div className="space-y-3">
            {[
              { 
                step: 1, 
                name: t("Opprett kompetansedefinisjoner"), 
                description: t("Start med å definere hvilke kompetanser som finnes i organisasjonen din"),
                icon: AcademicCapIcon,
                color: "bg-green-50 border-green-200 text-green-800"
              },
              { 
                step: 2, 
                name: t("Opprett roller"), 
                description: t("Definer roller i organisasjonen din og knytt kompetansekrav til dem"),
                icon: UserGroupIcon,
                color: "bg-purple-50 border-purple-200 text-purple-800"
              },
              { 
                step: 3, 
                name: t("Tildel roller til ansatte"), 
                description: t("Knytt ansatte til roller i organisasjonen"),
                icon: UserGroupIcon,
                color: "bg-blue-50 border-blue-200 text-blue-800"
              },
              { 
                step: 4, 
                name: t("Registrer ansatt kompetanse"), 
                description: t("Registrer hvilke ansatte som har hvilke kompetanser, inkludert datoer"),
                icon: DocumentCheckIcon,
                color: "bg-indigo-50 border-indigo-200 text-indigo-800"
              },
              { 
                step: 5, 
                name: t("Følg opp og forny"), 
                description: t("Bruk kompetansematrisen og utløpsvarsler for å følge opp kompetanse"),
                icon: CalendarIcon,
                color: "bg-yellow-50 border-yellow-200 text-yellow-800"
              },
            ].map((workflowStep) => (
              <div key={workflowStep.step} className={`flex items-start space-x-3 border rounded-lg p-4 ${workflowStep.color}`}>
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white flex items-center justify-center text-gray-600 font-semibold text-sm border-2 border-current">
                  {workflowStep.step}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <workflowStep.icon className="h-5 w-5" />
                    <div className="font-semibold text-sm">{workflowStep.name}</div>
                  </div>
                  <div className="text-xs">{workflowStep.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: "creating",
      title: t("Opprette kompetanse"),
      icon: PlusIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {t("Følg disse trinnene for å opprette en ny kompetansedefinisjon:")}
          </p>
          <ol className="list-decimal list-inside space-y-3 text-sm text-gray-700">
            <li>
              <strong>{t("Gå til Kompetansedefinisjoner:")}</strong> {t("Klikk på 'Kompetansedefinisjoner' i admin-menyen eller sidebar")}
            </li>
            <li>
              <strong>{t("Klikk 'Ny kompetanse':")}</strong> {t("Klikk på 'Ny kompetanse' knappen")}
            </li>
            <li>
              <strong>{t("Fyll ut skjema:")}</strong>
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li>{t("Navn (påkrevd) - f.eks. 'FSE-Kurs'")}</li>
                <li>{t("Beskrivelse - beskriv hva kompetansen innebærer")}</li>
                <li>{t("Kategori - velg kategori (Sikkerhet, Kvalitet, etc.)")}</li>
                <li>{t("Type - velg type (CV, Sertifikat, Kurs, Lisens, Annet)")}</li>
                <li>{t("Krever fornyelse - huk av hvis kompetansen må fornyes")}</li>
                <li>{t("Fornyelsesintervall - sett antall dager før fornyelse (f.eks. 1095 for 3 år)")}</li>
              </ul>
            </li>
            <li>
              <strong>{t("Lagre:")}</strong> {t("Klikk 'Opprett' for å lagre kompetansedefinisjonen")}
            </li>
          </ol>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-blue-900 mb-2">{t("Tips:")}</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-blue-800">
              <li>{t("Bruk beskrivende navn som gjør det lett å identifisere kompetansen")}</li>
              <li>{t("Legg til beskrivelse for å gi mer kontekst")}</li>
              <li>{t("Sett fornyelsesintervall for kompetanser som utløper (f.eks. sertifikater)")}</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: "assigning",
      title: t("Tildele kompetanse"),
      icon: DocumentCheckIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {t("Følg disse trinnene for å tildele kompetanse til en ansatt:")}
          </p>
          <ol className="list-decimal list-inside space-y-3 text-sm text-gray-700">
            <li>
              <strong>{t("Gå til Ansattoversikt:")}</strong> {t("Klikk på 'Ansattoversikt' i admin-menyen")}
            </li>
            <li>
              <strong>{t("Velg ansatt:")}</strong> {t("Klikk på ansatten du vil tildele kompetanse til")}
            </li>
            <li>
              <strong>{t("Klikk 'Legg til kompetanse':")}</strong> {t("Klikk på 'Legg til kompetanse' knappen")}
            </li>
            <li>
              <strong>{t("Velg kompetanse:")}</strong> {t("Velg kompetansedefinisjonen fra listen")}
            </li>
            <li>
              <strong>{t("Fyll ut informasjon:")}</strong>
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li>{t("Utstedelsesdato - når kompetansen ble utstedt")}</li>
                <li>{t("Utløpsdato - når kompetansen utløper (la stå tom for permanent)")}</li>
                <li>{t("Sertifikatnummer - hvis relevant")}</li>
                <li>{t("Notater - eventuelle notater om kompetansen")}</li>
              </ul>
            </li>
            <li>
              <strong>{t("Lagre:")}</strong> {t("Klikk 'Tildel' for å lagre kompetansen")}
            </li>
          </ol>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-green-900 mb-2">{t("Tips:")}</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-green-800">
              <li>{t("Sett alltid utstedelsesdato for å spore alder på kompetanse")}</li>
              <li>{t("Sett utløpsdato hvis kompetansen har en utløpsdato")}</li>
              <li>{t("Systemet oppdaterer automatisk status basert på utløpsdato")}</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: "training-plan",
      title: t("Opplæringsplan"),
      icon: CalendarIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {t("Opplæringsplanen hjelper deg med å planlegge og spore opplæring for ansatte på årsbasis.")}
          </p>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <h4 className="font-semibold text-indigo-900 mb-2">{t("Hva er opplæringsplan?")}</h4>
            <p className="text-sm text-indigo-800 mb-2">
              {t("Opplæringsplanen er en årlig plan for hvilke kompetanser ansatte skal oppnå eller fornye i løpet av året.")}
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-indigo-800">
              <li>{t("Planlegg opplæring på forhånd for å sikre at ansatte har nødvendig kompetanse")}</li>
              <li>{t("Spor fremdrift på opplæring gjennom året")}</li>
              <li>{t("Identifiser kompetansebehov basert på roller og utløpsdatoer")}</li>
            </ul>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-blue-900 mb-2">{t("Status typer:")}</h4>
            <div className="grid grid-cols-2 gap-3">
              {[
                { status: "planned", name: t("Planlagt"), description: t("Opplæring er planlagt"), color: "bg-gray-100 text-gray-800" },
                { status: "in_progress", name: t("Pågående"), description: t("Opplæring pågår"), color: "bg-blue-100 text-blue-800" },
                { status: "completed", name: t("Fullført"), description: t("Opplæring er fullført"), color: "bg-green-100 text-green-800" },
                { status: "cancelled", name: t("Avlyst"), description: t("Opplæring er avlyst"), color: "bg-red-100 text-red-800" },
              ].map((status) => (
                <div key={status.status} className={`border rounded-lg p-3 ${status.color}`}>
                  <div className="font-semibold text-sm">{status.name}</div>
                  <div className="text-xs mt-1">{status.description}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-green-900 mb-2">{t("Hvordan opprette opplæringsplan:")}</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-green-800">
              <li>{t("Gå til Opplæringsplan i admin-menyen")}</li>
              <li>{t("Klikk 'Legg til opplæring'")}</li>
              <li>{t("Velg ansatt som skal ha opplæringen")}</li>
              <li>{t("Velg kompetanse som skal oppnås eller fornyes")}</li>
              <li>{t("Sett planlagt dato for når opplæringen skal gjennomføres")}</li>
              <li>{t("Velg status (vanligvis 'Planlagt' ved opprettelse)")}</li>
              <li>{t("Legg til notater hvis nødvendig")}</li>
              <li>{t("Klikk 'Opprett' for å lagre")}</li>
            </ol>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-yellow-900 mb-2">{t("Tips:")}</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-yellow-800">
              <li>{t("Bruk opplæringsplanen sammen med utløpsvarsler for å planlegge fornyelse")}</li>
              <li>{t("Oppdater status når opplæring starter eller fullføres")}</li>
              <li>{t("Filtrer på år for å se opplæringsplan for spesifikke år")}</li>
              <li>{t("Bruk notater for å legge til ekstra informasjon om opplæringen")}</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: "matrix",
      title: t("Kompetansematrise"),
      icon: ChartBarIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {t("Kompetansematrisen gir en oversikt over hvilke ansatte som har de påkrevde kompetansene for sine roller.")}
          </p>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-semibold text-purple-900 mb-2">{t("Hvordan bruke matrisen:")}</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-purple-800">
              <li>{t("Velg en rolle fra listen")}</li>
              <li>{t("Se hvilke kompetanser som er påkrevd for rollen")}</li>
              <li>{t("Se hvilke ansatte som har rollen")}</li>
              <li>{t("Se kompetansestatus for hver ansatt")}</li>
            </ol>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            {[
              { status: "complete", name: t("Kompetent"), description: t("Alle krav oppfylt"), color: "bg-green-100 text-green-800" },
              { status: "partial", name: t("Delvis"), description: t("Noen krav mangler"), color: "bg-yellow-100 text-yellow-800" },
              { status: "incomplete", name: t("Ikke kompetent"), description: t("Mange krav mangler"), color: "bg-red-100 text-red-800" },
            ].map((status) => (
              <div key={status.status} className={`border rounded-lg p-3 ${status.color}`}>
                <div className="font-semibold text-sm">{status.name}</div>
                <div className="text-xs mt-1">{status.description}</div>
              </div>
            ))}
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-blue-900 mb-2">{t("Legge til kompetansekrav:")}</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
              <li>{t("Gå til kompetansematrisen")}</li>
              <li>{t("Velg en rolle")}</li>
              <li>{t("Klikk 'Legg til kompetanse'")}</li>
              <li>{t("Velg kompetansedefinisjoner fra listen")}</li>
              <li>{t("Kompetansene blir automatisk knyttet til rollen")}</li>
            </ol>
          </div>
        </div>
      ),
    },
  ];

  const [activeSection, setActiveSection] = useState("overview");

  return (
    <HsePageChrome
      title={t("Kompetansehåndtering Guide")}
      subtitle={t("Lær hvordan kompetansehåndteringssystemet fungerer")}
      backButton={{
        label: t("Tilbake til kompetanse"),
        action: () => navigate("/dashboard/hse-ks/competency"),
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mt-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <CompetencySubnav />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left sidebar - Information sections */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg p-4 sticky top-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t("Informasjon")}
              </h3>
              <nav className="space-y-1">
                {informationSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeSection === section.id
                        ? "bg-blue-100 text-blue-900"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <section.icon className="h-5 w-5" />
                      <span>{section.title}</span>
                    </div>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main content - Visual map and active section */}
          <div className="lg:col-span-2 space-y-6">
            {/* Visual Structure Map */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t("Kompetansestruktur")}
              </h3>
              <div
                className="border-2 border-gray-300 rounded-lg relative bg-gray-50 overflow-auto"
                style={{ width: "100%", height: "500px", minHeight: "500px" }}
              >
                <svg
                  width="100%"
                  height="100%"
                  style={{ position: "absolute", top: 0, left: 0, minWidth: "1000px" }}
                >
                  {/* Render connections */}
                  {connections.map((conn) => {
                    const path = calculatePath(conn.from, conn.to);
                    const fromPos = getNodePosition(conn.from);
                    const toPos = getNodePosition(conn.to);
                    const midX = (fromPos.x + toPos.x) / 2;
                    const midY = (fromPos.y + toPos.y) / 2;
                    
                    return (
                      <g key={`${conn.from}-${conn.to}`}>
                        <path
                          d={path}
                          stroke="#6B7280"
                          strokeWidth="2"
                          fill="none"
                          markerEnd="url(#arrowhead)"
                        />
                        <text
                          x={midX}
                          y={midY - 5}
                          textAnchor="middle"
                          className="text-xs fill-gray-600 font-medium"
                        >
                          {conn.label}
                        </text>
                      </g>
                    );
                  })}
                  <defs>
                    <marker
                      id="arrowhead"
                      markerWidth="10"
                      markerHeight="10"
                      refX="9"
                      refY="3"
                      orient="auto"
                    >
                      <polygon points="0 0, 10 3, 0 6" fill="#6B7280" />
                    </marker>
                  </defs>
                </svg>

                {/* Render nodes */}
                {structureNodes.map((node) => {
                  const isSelected = selectedNode === node.id;
                  const isHovered = hoveredNode?.id === node.id;
                  
                  return (
                    <div
                      key={node.id}
                      style={{
                        position: "absolute",
                        left: node.x,
                        top: node.y,
                        width: node.width,
                        height: node.height,
                        backgroundColor: node.color,
                        border: isSelected
                          ? "3px solid #1E40AF"
                          : isHovered
                          ? "2px solid #3B82F6"
                          : "2px solid #1E40AF",
                        borderRadius: "8px",
                        padding: "12px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        transform: isHovered ? "scale(1.05)" : "scale(1)",
                        boxShadow: isSelected
                          ? "0 4px 6px rgba(0, 0, 0, 0.1)"
                          : "0 2px 4px rgba(0, 0, 0, 0.05)",
                      }}
                      onClick={() => handleNodeClick(node)}
                      onMouseEnter={() => handleNodeHover(node)}
                      onMouseLeave={handleNodeLeave}
                    >
                      <div className="text-white font-semibold text-sm mb-1">
                        {node.title}
                      </div>
                      <div className="text-white text-xs opacity-90">
                        {node.description}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Node details */}
              {selectedNode && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  {(() => {
                    const node = structureNodes.find((n) => n.id === selectedNode);
                    if (!node) return null;
                    
                    const details = {
                      company: {
                        title: t("Firma"),
                        description: t("Ditt firma er toppnivået i strukturen. Alle kompetansedefinisjoner og roller tilhører firmaet."),
                        tips: [
                          t("Firmaet kan ha flere kompetansedefinisjoner"),
                          t("Firmaet kan ha flere roller"),
                          t("Alle ansatte tilhører firmaet"),
                        ],
                      },
                      definition: {
                        title: t("Kompetansedefinisjon"),
                        description: t("Kompetansedefinisjoner er maler for hvilke typer kompetanse som finnes. De definerer hva kompetansen er, men ikke hvem som har den."),
                        tips: [
                          t("Eksempler: FSE-Kurs, Førerkort klasse B, Førstehjelpskurs"),
                          t("Kan ha fornyelsesintervall hvis kompetansen utløper"),
                          t("Kan tilhøre forskjellige kategorier og typer"),
                        ],
                      },
                      role: {
                        title: t("Rolle"),
                        description: t("Roller definerer hvilke kompetanser som er påkrevd for forskjellige stillinger. Ansatte kan ha en eller flere roller."),
                        tips: [
                          t("Eksempler: Prosjektleder, Montør, Kranfører"),
                          t("Hver rolle kan ha flere påkrevde kompetanser"),
                          t("Ansatte kan ha flere roller"),
                        ],
                      },
                      requirement: {
                        title: t("Kompetansekrav"),
                        description: t("Kompetansekrav knytter kompetansedefinisjoner til roller. De definerer hvilke kompetanser som er påkrevd for en rolle."),
                        tips: [
                          t("En rolle kan ha flere kompetansekrav"),
                          t("Samme kompetanse kan være påkrevd for flere roller"),
                          t("Kompetansekrav brukes i kompetansematrisen"),
                        ],
                      },
                      competency: {
                        title: t("Ansatt kompetanse"),
                        description: t("Ansatt kompetanse registrerer faktisk kompetanse for ansatte. Den knytter en ansatt til en kompetansedefinisjon med datoer og status."),
                        tips: [
                          t("Inkluderer utstedelses- og utløpsdatoer"),
                          t("Har status (aktiv, utløper snart, utløpt)"),
                          t("Kan ha sertifikatnummer og notater"),
                        ],
                      },
                      employee: {
                        title: t("Ansatt"),
                        description: t("Ansatte er brukere i systemet som kan ha kompetanse tildelt. De kan ha en eller flere roller."),
                        tips: [
                          t("Ansatte kan ha flere kompetanser"),
                          t("Ansatte kan ha flere roller"),
                          t("Kompetansestatus beregnes basert på roller og kompetanse"),
                        ],
                      },
                    };
                    
                    const detail = details[node.type];
                    if (!detail) return null;
                    
                    return (
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-2">{detail.title}</h4>
                        <p className="text-sm text-blue-800 mb-3">{detail.description}</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-blue-700">
                          {detail.tips.map((tip, index) => (
                            <li key={index}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Active Information Section */}
            <div className="bg-white shadow rounded-lg p-6">
              {informationSections
                .filter((section) => section.id === activeSection)
                .map((section) => (
                  <div key={section.id}>
                    <div className="flex items-center space-x-3 mb-4">
                      <section.icon className="h-6 w-6 text-blue-600" />
                      <h3 className="text-xl font-semibold text-gray-900">
                        {section.title}
                      </h3>
                    </div>
                    <div className="prose prose-sm max-w-none">
                      {section.content}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </HsePageChrome>
  );
};

